/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */

import { Dispatch, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router';
import { notaryAPI } from '../../../../api/notary';
import { participantAPI } from '../../../../api/participant';
import { useRootSelector } from '../../../../hooks/useRootSelector';
import { isNotaryRoute } from '@utils/routing';
import { get } from '@utils/lodash';

export const useOneSpanSigning = (setSigningUrl: Dispatch<string>) => {
  const location = useLocation();
  const isNotary = isNotaryRoute(location.pathname);
  const { signingRoom, callDetails } = useRootSelector(state => state);
  const { signingAction } = signingRoom;

  const receiveMessage = useCallback(
    async (event: any) => {
      if (process.env.NODE_ENV === 'development' && event?.data?.source.startsWith('react-devtools')) {
        return;
      }
      const origin = event.origin || event.originalEvent.origin;
      const data = event.data;

      switch (data) {
        // required to begin listening to OneSpan iframe events
        case 'ESL:MESSAGE:REGISTER':
          event.source.postMessage('ESL:MESSAGE:ACTIVATE_EVENTS', origin);
          break;
        case 'ESL:MESSAGE:SUCCESS:SIGNER_COMPLETE':
          event.source.postMessage(data, origin);
          if (signingAction.document) {
            if (isNotary) {
              await notaryAPI.documents.handleSigning(callDetails!.id, signingAction.document.id);
              setSigningUrl('');
              return;
            }

            if (signingAction.participantSessionId) {
              await participantAPI.documents.handleSigning(
                callDetails!.id,
                signingAction.document.id,
                signingAction.participantSessionId,
              );
              setSigningUrl('');
              return;
            }
          }
          break;
        default:
          event.source.postMessage(data, origin);
          break;
      }
    },
    [signingAction.participantSessionId, signingAction.type, get(signingAction.document, 'id')],
  );

  useEffect(() => {
    window.addEventListener('message', receiveMessage, false);
    return () => window.removeEventListener('message', receiveMessage);
  }, [receiveMessage]);
};
